/* Full-screen presentation deck (cover slide + one slide per product).
   Ported from the Claude design's Present. */
import { useEffect, useMemo, useState } from 'react';
import { Ico } from './Icons';
import { useAuth } from '@/portal/auth/AuthContext';
import { ProductImage } from './ProductPeek';
import { orderByCategory } from '@/portal/data/product';
import type { PortalProduct, PortfolioDisplay } from '@/portal/api/types';

const LOGO = '/assets/didiers-logo.svg';

export interface PresentMeta {
  title: string;
  client?: string | null;
  event?: string | null;
  note?: string | null;
}

export function Present({
  meta,
  products,
  display,
  onClose,
}: {
  meta: PresentMeta;
  products: PortalProduct[];
  display: PortfolioDisplay;
  onClose: () => void;
}) {
  const { user } = useAuth();
  const company = user?.company_name || user?.name || user?.username || '';
  const ordered = useMemo(() => orderByCategory(products), [products]);
  const slides: Array<{ cover: true } | PortalProduct> = [{ cover: true }, ...ordered];
  const [i, setI] = useState(0);
  const go = (n: number) => setI(Math.max(0, Math.min(slides.length - 1, n)));

  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight') setI((p) => Math.min(slides.length - 1, p + 1));
      else if (e.key === 'ArrowLeft') setI((p) => Math.max(0, p - 1));
      else if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', h);
    return () => document.removeEventListener('keydown', h);
  }, [slides.length, onClose]);

  const slide = slides[i];
  const isCover = 'cover' in slide;
  const d = display || {};

  return (
    <div className="pl-present">
      <div className="pl-present-top">
        {isCover ? <span /> : <img src={LOGO} alt="Didier's" />}
        <button
          className="pl-btn ghost"
          style={{ color: '#fff', borderColor: 'rgba(255,255,255,0.25)' }}
          onClick={onClose}
        >
          <Ico.x width={16} height={16} /> Exit
        </button>
      </div>

      {isCover ? (
        <div
          className="pl-present-stage"
          style={{ gridTemplateColumns: '1fr', textAlign: 'center', placeItems: 'center' }}
        >
          <div className="pl-present-info" style={{ maxWidth: 720 }}>
            <div className="pl-cover-cobrand">
              {company && <div className="pl-cover-company">{company}</div>}
              <div className="pl-cover-collab">in collaboration with</div>
              <img className="pl-cover-logo" src={LOGO} alt="Didier's" />
            </div>
            {meta.client && (
              <div className="ey" style={{ fontSize: 26 }}>
                {meta.client}
              </div>
            )}
            <h1 style={{ fontSize: 60 }}>{meta.title}</h1>
            {meta.event && <p style={{ fontSize: 20 }}>{meta.event}</p>}
            {meta.note && <p style={{ marginTop: 28, fontStyle: 'italic' }}>{meta.note}</p>}
            <p style={{ marginTop: 30, color: 'var(--pink)' }}>
              {products.length} selections · Use → to begin
            </p>
          </div>
        </div>
      ) : (
        <div className="pl-present-stage">
          <div className="pl-present-img">
            <ProductImage src={slide.imgHi || slide.img} alt={slide.name} />
          </div>
          <div className="pl-present-info">
            {d.category !== false && <div className="ey">{slide.rangeLabel}</div>}
            <h1>{slide.name}</h1>
            <div className="cd">{slide.code}</div>
            {slide.subtitle && <p className="sub">{slide.subtitle}</p>}
            {slide.desc && <p>{slide.desc}</p>}
            <div className="pl-present-allergen">
              {slide.allergens.length ? (
                <span>
                  <b>Contains:</b> {slide.allergens.join(', ')}
                </span>
              ) : (
                <span>No declared allergens</span>
              )}
            </div>
            {(d.packSize || d.weight || d.ingredients) && (
              <dl className="pl-present-specs">
                {d.packSize && slide.packSize && (
                  <div className="row2">
                    <span className="k">Case size</span>
                    <span>{slide.packSize}</span>
                  </div>
                )}
                {d.weight && slide.weight && (
                  <div className="row2">
                    <span className="k">Weight</span>
                    <span>{slide.weight}</span>
                  </div>
                )}
                {d.ingredients && slide.ingredients && (
                  <div className="row2">
                    <span className="k">Ingredients</span>
                    <span dangerouslySetInnerHTML={{ __html: slide.ingredients }} />
                  </div>
                )}
              </dl>
            )}
          </div>
        </div>
      )}

      <div className="pl-present-nav">
        <button onClick={() => go(i - 1)} disabled={i === 0}>
          <Ico.arrowL width={20} height={20} />
        </button>
        <span className="count">
          {i + 1} / {slides.length}
        </span>
        <button onClick={() => go(i + 1)} disabled={i === slides.length - 1}>
          <Ico.arrowR width={20} height={20} />
        </button>
      </div>
      <div className="pl-present-dots">
        {slides.map((_, n) => (
          <button key={n} className={'d' + (n === i ? ' on' : '')} onClick={() => go(n)} />
        ))}
      </div>
    </div>
  );
}
